import { useNavigate } from 'react-router-dom';
import { copyableErrorToast } from '@/lib/copyableErrorToast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Content, Conversation } from '@shared/types';
import * as Sentry from '@sentry/react';
import { useSendContentMutation } from '@/services/messageService';
import { setCachedOutputFormat } from '@/lib/outputFormatCache';
import { setLatticePreview } from './latticePreviewStore';
import { inferColor } from '@/lib/colorInference';
import { INLET_LABELS } from '@/config/supin';
import { SUSP_TYPE_LABELS } from '@/config/shark';

type VehicleSubtype =
  | 'tubeframe-joint'
  | 'nasa-supersonic-inlet'
  | 'suspension-kinematics'
  | undefined;

interface UseGenerateConversationArgs {
  type: 'parametric' | 'imagetocad';
  model: string;
  vehicleSubtype: VehicleSubtype;
}

export function useGenerateConversation({
  type,
  model,
  vehicleSubtype,
}: UseGenerateConversationArgs) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const newConversationId = useMemo(() => {
    return crypto.randomUUID();
  }, []);

  const { mutate: sendMessage } = useSendContentMutation({
    conversation: {
      id: newConversationId,
      user_id: user?.id ?? '',
      type: type,
      settings: {
        model: model,
        ...(vehicleSubtype ? { vehicleSubtype } : {}),
      },
      current_message_leaf_id: null,
    },
  });

  const { mutate: handleGenerate, isPending: isGenerating } = useMutation({
    mutationFn: async (content: Content) => {
      const getRealUserId = async (): Promise<string> => {
        const {
          data: { session: cur },
        } = await supabase.auth.getSession();
        const id = cur?.user?.id;
        if (id && id !== '__god_mode__') return id;
        const { data: anonData, error: anonError } =
          await supabase.auth.signInAnonymously();
        if (anonData?.session?.user?.id) return anonData.session.user.id;
        throw new Error(
          anonError?.message
            ? `Auth failed: ${anonError.message}`
            : 'Anonymous sign-in not available. Enable it in Supabase > Authentication > Providers.',
        );
      };
      const realUserId = await getRealUserId();

      const outputFormat = content.outputFormat ?? 'solidbody';
      const color = inferColor(content.text ?? '');

      // For SUPIN runs we know the title upfront from the inlet type — set it
      // at insert time so the tab label never shows the placeholder. For
      // everything else we keep "New Conversation" and let the title-generator
      // Edge Function fill it in async in onSuccess.
      const initialTitle = content.supinParams
        ? `NASA SUPIN ${INLET_LABELS[content.supinParams.inletType] ?? content.supinParams.inletType}`
        : content.suspensionKinematicsParams
          ? `Suspension Kinematics — ${SUSP_TYPE_LABELS[content.suspensionKinematicsParams.suspType] ?? content.suspensionKinematicsParams.suspType} ${content.suspensionKinematicsParams.axle === 'front' ? 'Front' : 'Rear'}`
          : 'New Conversation';

      const { data: conversation, error: conversationError } = await supabase
        .from('conversations')
        .insert([
          {
            id: newConversationId,
            user_id: realUserId,
            title: initialTitle,
            type: type,
            settings: {
              model: model,
              outputFormat: outputFormat,
              color,
            },
          },
        ])
        .select()
        .single();

      if (conversationError) throw conversationError;

      // Seamless handoff (#309): seed the conversation query cache under the SAME
      // key EditorView reads (`['conversation', id, userId]`) so it resolves from
      // cache on first render — no blocking spinner between Generate→navigate and
      // the viewer mounting, so the lattice standby paints immediately. EditorView
      // still refetches in the background; this just covers the first frame.
      queryClient.setQueryData(
        ['conversation', newConversationId, realUserId],
        conversation,
      );

      setCachedOutputFormat(newConversationId, outputFormat);

      // Lattice converter (#309): stash the original-part preview URL keyed by the
      // new conversation so the editor's generation overlay can show the original
      // part through the ~20s solve (it lives only in prompt state, not the
      // persisted message). Cleared when the lattice paints.
      if (content.implicitPrimitiveParams?.kind === 'lattice-fill') {
        const p = content.implicitPrimitiveParams.params as Record<
          string,
          string | undefined
        >;
        const url = p?.previewUrl;
        // #315: carry the STEP's B-rep edges so the during-solve overlay can
        // draw real topological edges on the original part.
        if (url)
          setLatticePreview(newConversationId, {
            url,
            edgesB64: p?.edgesB64,
            tangentEdgesB64: p?.tangentEdgesB64,
          });
      }

      sendMessage(content);

      return {
        conversationId: conversation.id,
        content: content,
      };
    },
    onSuccess: (data) => {
      // SUPIN runs already got a meaningful title at insert time; the LLM
      // title-generator would only delay things and possibly overwrite a
      // shorter, more accurate title.
      const isSupin = !!data.content.supinParams;
      if (!isSupin) {
        supabase.functions
          .invoke('title-generator', {
            body: {
              content: data.content,
              conversationId: data.conversationId,
            },
          })
          .then(({ data: titleData, error }) => {
            if (
              !error &&
              titleData?.title &&
              titleData.title !== 'New Conversation'
            ) {
              supabase
                .from('conversations')
                .update({ title: titleData.title })
                .eq('id', data.conversationId)
                .then(() => {
                  queryClient.invalidateQueries({
                    queryKey: ['conversations'],
                  });
                  // Prefix filter so the title reaches EditorView's
                  // user-scoped ['conversation', id, user?.id] entry, not just
                  // the bare ['conversation', id] phantom (#295 key mismatch).
                  queryClient.setQueriesData(
                    { queryKey: ['conversation', data.conversationId] },
                    (oldConversation: Conversation | undefined) =>
                      oldConversation
                        ? { ...oldConversation, title: titleData.title }
                        : oldConversation,
                  );
                });
            }
          })
          .catch((error) => {
            console.error('Error generating title:', error);
          });
      }
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      navigate(`/editor/${data.conversationId}`);
    },
    onError: (error) => {
      Sentry.captureException(error);
      copyableErrorToast(error, {
        fallbackMessage: 'Failed to process prompt',
      });
    },
  });

  return { newConversationId, handleGenerate, isGenerating };
}
